import React, {useState, useEffect, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import './LanguageSelector.css';

// Ordered by native name so each script (Latin, Cyrillic, Greek, Arabic, Devanagari, Thai, CJK)
// clusters together — a native speaker scans their own script.
const LANGUAGES = [
	{locale: 'da-DK', localName: 'Dansk', englishName: 'Danish'},
	{locale: 'de-DE', localName: 'Deutsch', englishName: 'German'},
	{locale: 'en-US', localName: 'English', englishName: 'English'},
	{locale: 'es-ES', localName: 'Español', englishName: 'Spanish'},
	{locale: 'fr-FR', localName: 'Français', englishName: 'French'},
	{locale: 'it-IT', localName: 'Italiano', englishName: 'Italian'},
	{locale: 'ku-TR', localName: 'Kurdî', englishName: 'Kurdish (Kurmanji)'},
	{locale: 'hu-HU', localName: 'Magyar', englishName: 'Hungarian'},
	{locale: 'nl-NL', localName: 'Nederlands', englishName: 'Dutch'},
	{locale: 'no-NO', localName: 'Norsk', englishName: 'Norwegian'},
	{locale: 'pl-PL', localName: 'Polski', englishName: 'Polish'},
	{locale: 'pt-PT', localName: 'Português', englishName: 'Portuguese'},
	{locale: 'sq-AL', localName: 'Shqip', englishName: 'Albanian'},
	{locale: 'sr-RS', localName: 'Srpski', englishName: 'Serbian'},
	{locale: 'fi-FI', localName: 'Suomi', englishName: 'Finnish'},
	{locale: 'sv-SE', localName: 'Svenska', englishName: 'Swedish'},
	{locale: 'vi-VN', localName: 'Tiếng Việt', englishName: 'Vietnamese'},
	{locale: 'tr-TR', localName: 'Türkçe', englishName: 'Turkish'},
	{locale: 'mk-MK', localName: 'Македонски', englishName: 'Macedonian'},
	{locale: 'ru-RU', localName: 'Русский', englishName: 'Russian'},
	{locale: 'uk-UA', localName: 'Українська', englishName: 'Ukrainian'},
	{locale: 'el-GR', localName: 'Ελληνικά', englishName: 'Greek'},
	{locale: 'ar-AE', localName: 'العربية', englishName: 'Arabic'},
	{locale: 'ur-PK', localName: 'اردو', englishName: 'Urdu'},
	{locale: 'fa-IR', localName: 'فارسی', englishName: 'Persian'},
	{locale: 'ps-AF', localName: 'پښتو', englishName: 'Pashto'},
	{locale: 'ckb-IQ', localName: 'سۆرانی', englishName: 'Kurdish (Sorani)'},
	{locale: 'hi-IN', localName: 'हिन्दी', englishName: 'Hindi'},
	{locale: 'th-TH', localName: 'ไทย', englishName: 'Thai'},
	{locale: 'zh-CN', localName: '中文', englishName: 'Chinese'},
];

const MOBILE_BREAKPOINT = '(max-width: 720px)';

function useMediaQuery(query) {
	const [matches, setMatches] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false));
	useEffect(() => {
		const mq = window.matchMedia(query);
		const handler = e => setMatches(e.matches);
		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, [query]);
	return matches;
}

const GlobeIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true">
		<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
	</svg>
);

const CloseIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" aria-hidden="true">
		<path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
	</svg>
);

const SearchIconSvg = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
		<path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
	</svg>
);

const codeLabel = locale => {
	const c = locale.split('-')[0];
	return c.charAt(0).toUpperCase() + c.slice(1);
};

function changeLanguage(locale) {
	let newLang = locale.split('-')[0];
	// We only ship /nb/, not /no/. The selector still shows "Norsk (no-NO)"
	// for familiarity but routes to the canonical Bokmål URL.
	if (newLang === 'no') newLang = 'nb';
	try {
		localStorage.setItem('pifa-language', newLang);
		localStorage.setItem('i18nextLng', newLang);
	} catch (e) {
		// localStorage unavailable (private mode) — non-critical.
	}
	window.location.href = newLang === 'sv' ? '/' : `/${newLang}/`;
}

export const LanguageSelector = ({color, label}) => {
	const {t, i18n} = useTranslation();
	const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [dropdownPos, setDropdownPos] = useState({top: 0, right: 0});
	const triggerRef = useRef(null);
	const dropdownRef = useRef(null);

	// Anchor the portaled dropdown to the trigger's viewport rect. Recompute
	// on resize so the dropdown stays glued to the button as layout changes.
	useEffect(() => {
		if (!dropdownOpen || !triggerRef.current) return;
		const updatePos = () => {
			if (!triggerRef.current) return;
			const rect = triggerRef.current.getBoundingClientRect();
			setDropdownPos({top: rect.bottom + 6, right: window.innerWidth - rect.right});
		};
		updatePos();
		window.addEventListener('resize', updatePos);
		// Close on scroll — keeps the dropdown from floating away from the trigger
		// when the user starts scrolling.
		const onScroll = () => setDropdownOpen(false);
		window.addEventListener('scroll', onScroll, {passive: true});
		return () => {
			window.removeEventListener('resize', updatePos);
			window.removeEventListener('scroll', onScroll);
		};
	}, [dropdownOpen]);

	useEffect(() => {
		if (!dropdownOpen) return;
		const onClick = e => {
			const insideTrigger = triggerRef.current && triggerRef.current.contains(e.target);
			const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
			if (!insideTrigger && !insideDropdown) setDropdownOpen(false);
		};
		const onKey = e => {
			if (e.key === 'Escape') setDropdownOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onClick);
			document.removeEventListener('keydown', onKey);
		};
	}, [dropdownOpen]);

	useEffect(() => {
		if (!dialogOpen) return;
		const onKey = e => {
			if (e.key === 'Escape') setDialogOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [dialogOpen]);

	// Reset search whenever both surfaces are closed so the next open starts fresh.
	useEffect(() => {
		if (!dropdownOpen && !dialogOpen) setSearch('');
	}, [dropdownOpen, dialogOpen]);

	const filtered = useMemo(() => {
		if (!search.trim()) return LANGUAGES;
		const q = search.trim().toLowerCase();
		return LANGUAGES.filter(m => m.localName.toLowerCase().includes(q) || m.englishName.toLowerCase().includes(q) || m.locale.toLowerCase().includes(q));
	}, [search]);

	const openSelector = () => {
		if (isMobile) {
			setDialogOpen(true);
			setSearch('');
		} else {
			setDropdownOpen(prev => !prev);
		}
	};

	const dropdownPortal =
		dropdownOpen && !isMobile && typeof document !== 'undefined'
			? createPortal(
					<div ref={dropdownRef} className="lang-dropdown" role="listbox" style={{top: dropdownPos.top, right: dropdownPos.right}}>
						<div className="lang-dropdown-header">
							<span className="lang-dropdown-search-icon">
								<SearchIconSvg />
							</span>
							<input autoFocus type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('landing.nav.searchLanguage')} className="lang-dropdown-search" />
						</div>
						{filtered.length === 0 ? (
							<div className="lang-empty">{t('landing.nav.noMatches')}</div>
						) : (
							filtered.map(m => (
								<button
									key={m.locale}
									type="button"
									className="lang-item"
									role="option"
									aria-selected={m.locale === i18n.language}
									onClick={() => {
										setDropdownOpen(false);
										changeLanguage(m.locale);
									}}
								>
									<span className="lang-item-code">{codeLabel(m.locale)}</span>
									<span className="lang-item-name">{m.localName}</span>
									<span className="lang-item-en">{m.englishName}</span>
								</button>
							))
						)}
					</div>,
					document.body
				)
			: null;

	const dialogPortal =
		dialogOpen && typeof document !== 'undefined'
			? createPortal(
					<div className="lang-dialog" role="dialog" aria-modal="true" aria-label={t('landing.nav.language')}>
						<div className="lang-dialog-header">
							<span className="lang-dialog-search-icon">
								<SearchIconSvg />
							</span>
							<input autoFocus type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('landing.nav.searchLanguage')} className="lang-dialog-search" />
							<button type="button" className="lang-dialog-close" onClick={() => setDialogOpen(false)} aria-label={t('landing.nav.close')}>
								<CloseIcon />
							</button>
						</div>
						<div className="lang-dialog-list">
							{filtered.length === 0 ? (
								<div className="lang-empty">{t('landing.nav.noMatches')}</div>
							) : (
								filtered.map(m => (
									<button
										key={m.locale}
										type="button"
										className="lang-dialog-item"
										onClick={() => {
											setDialogOpen(false);
											changeLanguage(m.locale);
										}}
									>
										<span className="lang-dialog-item-code">{codeLabel(m.locale)}</span>
										<span className="lang-dialog-item-name">{m.localName}</span>
										<span className="lang-dialog-item-en">{m.englishName}</span>
									</button>
								))
							)}
						</div>
					</div>,
					document.body
				)
			: null;

	return (
		<span className="lang-selector">
			<button ref={triggerRef} type="button" onClick={openSelector} className="nav-icon-btn" aria-label={t('landing.nav.language')} title={t('landing.nav.language')} style={{color: color || 'inherit'}}>
				<GlobeIcon />
				{label && <span className="nav-icon-btn-label">{label}</span>}
			</button>
			{dropdownPortal}
			{dialogPortal}
		</span>
	);
};

export default LanguageSelector;
